import moment from "moment-timezone";

import { parseApiError } from "@/utils/parseApiError";
import { isDefinitiveAuthFailure, refreshAuthToken } from "@/utils/tokenRefreshManager";

import * as authApi from "../../api/auth";
import { handleError } from "../utils/error";

import {
  IS_AUTHENTICATED,
  IS_CHECKING_AUTH,
  IS_LOGGING_IN,
  RESET_APP,
  SET_ERROR_MESSAGE,
} from "./authentication.actionTypes";

export const setIsLoggingIn = (status) => (dispatch) => {
  dispatch({
    type: IS_LOGGING_IN,
    payload: status,
  });
};

export const setIsAuthenticated = (status) => (dispatch) => {
  dispatch({
    type: IS_AUTHENTICATED,
    payload: status,
  });
};

export const setIsCheckingAuth = (status) => (dispatch) => {
  dispatch({
    type: IS_CHECKING_AUTH,
    payload: status,
  });
};

export const setErrorMessage = (payload) => (dispatch) => {
  dispatch({
    type: SET_ERROR_MESSAGE,
    payload,
  });
};

export const resetApp = () => (dispatch) => {
  dispatch({
    type: RESET_APP,
  });
};

export const reset = () => (dispatch) => {
  window.localStorage.removeItem("auth");
  window.localStorage.removeItem("user");
  dispatch(setIsAuthenticated(false));
  dispatch(setIsCheckingAuth(false));
  dispatch(setIsLoggingIn(false));
  dispatch(resetApp());
};

export const login =
  ({ email, password }) =>
  async (dispatch) => {
    const data = {
      grant_type: "password",
      username: email,
      password,
    };

    // Use URLSearchParams for proper application/x-www-form-urlencoded encoding
    const params = new URLSearchParams();
    Object.keys(data).forEach((key) => {
      params.append(key, data[key]);
    });

    dispatch(setIsLoggingIn(true));

    try {
      const { data } = await authApi.tokenAuth(null, params);
      const nowUnix = moment.tz(moment.tz.guess()).unix();
      const authData = {
        ...data,
        time_sign_up: nowUnix,
        expires_at: data.expires_in ? nowUnix + data.expires_in : undefined,
      };

      window.localStorage.setItem("auth", JSON.stringify(authData));
      dispatch(setIsAuthenticated(true));
    } catch (errors) {
      const { response } = errors;
      if (response) {
        dispatch(setErrorMessage(response?.data?.error_description ?? "Something went wrong"));
        throw response.data;
      }

      dispatch(setErrorMessage("Something went wrong"));
      throw errors;
    } finally {
      dispatch(setIsLoggingIn(false));
    }
  };

export const checkAuth = () => async (dispatch) => {
  const token = JSON.parse(localStorage.getItem("auth"));

  if (token) {
    const today = moment.tz(moment.tz.guess()).unix();

    // Prefer the absolute expires_at written on login/refresh. Fall back to
    // the legacy time_sign_up + expires_in check for sessions that pre-date
    // the expires_at addition so those users are not force-refreshed on deploy.
    const isExpired = token.expires_at
      ? today > token.expires_at
      : token.time_sign_up && token.expires_in && today - token.time_sign_up > token.expires_in;

    if (isExpired) {
      try {
        await dispatch(refreshToken());
      } catch (error) {
        // refreshToken() already declines to end the session on transient
        // failures, but it rethrows every error so callers can react. Catching
        // everything here and resetting undid that: one 503 at page load wiped
        // the stored session, and the next reload landed on /login. Apply the
        // same test at the call site so only a definitive rejection (400/401)
        // clears it. On a transient failure the session survives with an
        // expired access token and the next API call retries the refresh.
        if (isDefinitiveAuthFailure(error)) {
          // The session is gone, so return instead of falling through. Marking
          // the user authenticated immediately after clearing their session let
          // the app render once as if signed in, and only the next reload sent
          // them to /login.
          dispatch(reset());
          return;
        }
      }
    }

    dispatch(setIsAuthenticated(true));
  } else {
    // Logout
    dispatch(logout());
    dispatch(reset());
  }
};

export const refreshToken = () => async (dispatch) => {
  try {
    await refreshAuthToken();
  } catch (error) {
    // Only destroy the session on definitive auth rejections (400/401).
    // Transient failures (network errors, 5xx) should not log the user out.
    if (isDefinitiveAuthFailure(error)) {
      dispatch(logout());
      dispatch(reset());
    }
    throw error;
  }
};

export const logout = () => async (dispatch) => {
  const token = JSON.parse(localStorage.getItem("auth"));
  const data = {
    token: JSON.stringify(token),
  };

  // Use URLSearchParams for proper application/x-www-form-urlencoded encoding
  const params = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    params.append(key, data[key]);
  });

  if (token) {
    try {
      await authApi.logout(params);
    } finally {
      dispatch(reset());
    }
  } else {
    dispatch(reset());
  }
};

export const generateGoogleToken = (payload) => async (dispatch) => {
  try {
    const { data } = await authApi.postGoogleToken(null, payload);

    dispatch(
      googleLogin({
        authToken: data.id_token,
      })
    );
  } catch (_error) {
    dispatch(setErrorMessage("Something went wrong"));
  }
};

export const googleLogin =
  ({ authToken }) =>
  async (dispatch) => {
    const data = {
      token: authToken,
    };

    // Use URLSearchParams for proper application/x-www-form-urlencoded encoding
    const params = new URLSearchParams();
    Object.keys(data).forEach((key) => {
      params.append(key, data[key]);
    });

    dispatch(setIsLoggingIn(true));

    try {
      const { data } = await authApi.googleLogin(null, params);
      const nowUnix = moment.tz(moment.tz.guess()).unix();
      const authData = {
        ...data,
        time_sign_up: nowUnix,
        expires_at: data.expires_in ? nowUnix + data.expires_in : undefined,
      };
      window.localStorage.setItem("auth", JSON.stringify(authData));
      dispatch(setIsAuthenticated(true));
    } catch (errors) {
      const { response } = errors;
      if (response) {
        dispatch(setErrorMessage(parseApiError(errors).message));
        throw response.data;
      }

      dispatch(setErrorMessage("Something went wrong"));
      throw errors;
    } finally {
      dispatch(setIsLoggingIn(false));
    }
  };

export const microsoftLogin = (payload) => async (dispatch) => {
  // Use URLSearchParams for proper application/x-www-form-urlencoded encoding
  const params = new URLSearchParams();
  Object.keys(payload).forEach((key) => {
    params.append(key, payload[key]);
  });

  dispatch(setIsLoggingIn(true));

  try {
    const { data } = await authApi.microsoftLogin(null, params);
    const nowUnix = moment.tz(moment.tz.guess()).unix();
    const authData = {
      ...data,
      time_sign_up: nowUnix,
      expires_at: data.expires_in ? nowUnix + data.expires_in : undefined,
    };
    window.localStorage.setItem("auth", JSON.stringify(authData));
    dispatch(setIsAuthenticated(true));
  } catch (errors) {
    const { response } = errors;
    if (response) {
      dispatch(setErrorMessage(parseApiError(errors).message));
      throw response.data;
    }

    dispatch(setErrorMessage("Something went wrong"));
  } finally {
    dispatch(setIsLoggingIn(false));
  }
};

export const fetchXeroAuthorizationUrl = () => async (dispatch) => {
  try {
    dispatch(setIsLoggingIn(true));
    const { data } = await authApi.getXeroAuthorizationUrl({ is_login: true })();
    return data;
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsLoggingIn(false));
  }
};

export const fetchXeroToken = () => async (dispatch) => {
  try {
    dispatch(setIsLoggingIn(true));
    const { data } = await authApi.getXeroAuthorizationUrl({ is_login: true })();
    return data;
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsLoggingIn(false));
  }
};

export const xeroLogin = (payload) => async (dispatch) => {
  dispatch(setIsLoggingIn(true));

  try {
    const {
      data: { token },
    } = payload.isLogin ? await authApi.getXeroToken(payload) : { data: { token: payload.token } };
    const { data } = await authApi.xeroLogin(null, { token });
    const nowUnix = moment.tz(moment.tz.guess()).unix();
    const authData = {
      ...data,
      time_sign_up: nowUnix,
      expires_at: data.expires_in ? nowUnix + data.expires_in : undefined,
    };
    window.localStorage.setItem("auth", JSON.stringify(authData));
    dispatch(setIsAuthenticated(true));
  } catch (errors) {
    const { response } = errors;
    if (response) {
      dispatch(setErrorMessage(parseApiError(errors).message));
      throw response.data;
    }

    dispatch(setErrorMessage("Something went wrong"));
  } finally {
    dispatch(setIsLoggingIn(false));
  }
};
